// import { Stack } from "@fastlooptech/layout-components";
import { Stack } from "@/layouts/LayoutComponents";
import { CarouselControl } from "./CarouselControl";
import { spaceUnit } from "@/helpers/utils.helper";

export interface CarouselControlsProps {
  "data-test-id"?: string;
  /**
   * The class name of the CarouselControls.
   * @type string
   */
  className?: string;
  /**
   * The total number of dots to be displayed by the CarouselControls component. This value should be greater than or equal to the activeIndex.
   * @type number
   * @required
   */
  total: number;
  /**
   * The index of the currently active dot. This value should be less than the total - 1 and greater than or equal to 0.
   * @default 0
   * @type number
   */
  activeIndex?: number;
  /**
   * The boolean that determines if the CarouselControls is disabled.
   * @type boolean
   * @optional
   * @default false
   */
  disabled?: boolean;
  /**
   * A callback function that is invoked when a dot is clicked. The index of the clicked dot is passed as an argument to this function.
   * @type (index: number) => void
   * @required
   */
  onClick?: (index: number) => void;
  /**
   * The boolean that determines if the CarouselControls has column orientation.
   * @type boolean
   * @optional
   * @default false
   */
  column?: boolean;
}

const getActiveIndex = (activeIndex: number = 0, total: number) => {
  if (activeIndex < 0) {
    return 0;
  }
  if (activeIndex > total - 1) {
    return total - 1;
  }
  return activeIndex;
};

export function CarouselControls(props: Readonly<CarouselControlsProps>) {
  const total = Math.round(props.total);
  const activeIndex = getActiveIndex(props.activeIndex, total);

  if (!total) return;
  if (total === 1) {
    return (
      <CarouselControl
        data-test-id={props["data-test-id"]}
        index={0}
        variant={"active"}
        className={props.className}
        disabled={props.disabled}
        onClick={() => props.onClick ? props.onClick(0) : null}
      />
    );
  }
  if (total > 1) {
    return (
      <Stack
        column={props.column}
        shrink
        grow
        data-test-id={props["data-test-id"]}
        className={props.className}
        role="group"
        gap={spaceUnit}
        hAlignContent="right"
      >
        {Array.from({ length: total }, (_, index) => (
          <CarouselControl
            key={index}
            index={index}
            variant={index === activeIndex ? "active" : "inactive"}
            disabled={props.disabled}
            onClick={() => props.onClick ? props.onClick(0) : null}
          />
        ))}
      </Stack>
    );
  }
}
